import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Calendar, Clock, User, Tag, AlertCircle, ArrowLeft, Loader, Download } from 'lucide-react';
import { getBlogPost } from '../lib/supabase';
import SEO, { generateArticleSchema } from './SEO';
import DynamicCTA from './DynamicCTA';
import ContactModal from './ContactModal';
import CalWidget from './CalWidget';
import EmailCaptureModal from './EmailCaptureModal';
import { supabase } from '../lib/supabase';
import CollapsibleFAQ from './CollapsibleFAQ';
import BlogDownloadSection from './BlogDownloadSection';

interface BlogPostProps {
  previewMode?: boolean;
  slug?: string; // Make slug optional since it can come from either props or params
}

export default function BlogPost({ previewMode = false, slug: propSlug }: BlogPostProps) {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isCalOpen, setIsCalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Use slug from props if provided, otherwise use from params
  const slug = propSlug || paramSlug;

  // Check if we're in preview mode (either from props or URL)
  const isPreview = previewMode || location.pathname.startsWith('/admin');

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return;
      
      try {
        setLoading(true);
        setError(null);
        console.log(`Fetching blog post: ${slug} (preview: ${isPreview})`);
        const data = await getBlogPost(slug, isPreview);
        setPost(data);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err instanceof Error ? err.message : 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug, isPreview]);

  useEffect(() => {
    // Fetch the PDF URL from the uploads table
    const fetchPdfUrl = async () => {
      try {
        const { data, error } = await supabase
          .from('uploads')
          .select('url')
          .eq('name', 'Nonprofit_AI_Ethics_Checklist_v2.pdf')
          .eq('deleted', false)
          .single();
        
        if (error) {
          console.error('Error fetching PDF URL:', error);
          // Fallback to the static file if database fetch fails
          setPdfUrl('/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf');
        } else if (data) {
          console.log('Found PDF in uploads table:', data);
          setPdfUrl(data.url);
        } else {
          console.warn('PDF not found in uploads table, using fallback');
          setPdfUrl('/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf');
        }
      } catch (err) {
        console.error('Error in fetchPdfUrl:', err);
        setPdfUrl('/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf');
      }
    };
    
    fetchPdfUrl();
  }, []);

  // Handle download button click from the processed content
  useEffect(() => {
    const handleOpenDownloadModal = () => {
      setIsDownloadModalOpen(true);
    };
    
    document.addEventListener('open-download-modal', handleOpenDownloadModal);
    
    return () => {
      document.removeEventListener('open-download-modal', handleOpenDownloadModal);
    };
  }, []);

  const handleTagClick = (tagName: string) => {
    navigate(`/blog?tag=${encodeURIComponent(tagName)}`);
  };

  const articleSchema = post ? generateArticleSchema({
    headline: post.title,
    description: post.excerpt || '',
    image: post.image_url || 'https://one80labs.com/default-blog-image.jpg',
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: post.blog_authors?.name || 'One80Labs Team',
    publisher: 'One80Labs'
  }) : null;

  // Generate FAQ schema if post has a FAQ section
  const generateFAQSchema = () => {
    if (!post || !post.content) return null;
    
    // Look for FAQ patterns in the content
    const faqRegex = /<h[2-4][^>]*>.*?FAQ.*?<\/h[2-4]>|<h[2-4][^>]*>.*?Frequently Asked Questions.*?<\/h[2-4]>/i;
    if (!faqRegex.test(post.content)) return null;
    
    // Try to extract question-answer pairs
    const questionRegex = /<h[3-5][^>]*>(.*?)<\/h[3-5]>.*?<p>(.*?)<\/p>/gs;
    const matches = [...post.content.matchAll(questionRegex)];
    
    if (matches.length === 0) return null;
    
    const faqItems = matches.map(match => ({
      "@type": "Question",
      "name": match[1].replace(/<[^>]*>/g, '').trim(),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": match[2].replace(/<[^>]*>/g, '').trim()
      }
    }));
    
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems
    };
  };

  // Local business schema for better local SEO
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "One80Labs - Omaha AI Agency",
    "image": "https://one80labs.com/logo.png",
    "description": "The Midwest's Premier AI Agency providing AI training, project management, and technology consulting services in Omaha, Nebraska and surrounding areas.",
    "@id": "https://one80labs.com",
    "url": "https://one80labs.com",
    "telephone": "+1-402-915-3613",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4383 Nicholas Street #302",
      "addressLocality": "Omaha",
      "addressRegion": "NE",
      "postalCode": "68181",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 41.2651,
      "longitude": -95.9397
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "17:00"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Omaha",
        "sameAs": "https://en.wikipedia.org/wiki/Omaha,_Nebraska"
      },
      {
        "@type": "City",
        "name": "Lincoln",
        "sameAs": "https://en.wikipedia.org/wiki/Lincoln,_Nebraska"
      },
      {
        "@type": "City",
        "name": "Council Bluffs",
        "sameAs": "https://en.wikipedia.org/wiki/Council_Bluffs,_Iowa"
      },
      {
        "@type": "State",
        "name": "Nebraska",
        "sameAs": "https://en.wikipedia.org/wiki/Nebraska"
      }
    ]
  };

  // Combine schemas if needed
  const getJsonLd = () => {
    const schemas = [];
    if (articleSchema) schemas.push(articleSchema);
    
    const faqSchema = generateFAQSchema();
    if (faqSchema) schemas.push(faqSchema);
    
    // Add local business schema for local SEO
    schemas.push(localBusinessSchema);
    
    // Add non-profit service schema
    if (slug === 'why-non-profits-cant-afford-ignore-ai-any-longer') {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Consulting for Non-Profits",
        "provider": {
          "@type": "Organization",
          "name": "One80Labs",
          "url": "https://one80labs.com"
        },
        "description": "Expert AI consulting services for non-profits, foundations, and grantmakers in Omaha, Nebraska and the Midwest region. We help organizations implement AI ethically and effectively.",
        "areaServed": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 41.2651,
            "longitude": -95.9397
          },
          "geoRadius": "500",
          "description": "Serving Omaha, Lincoln, Council Bluffs, and the greater Midwest region"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Free AI ethics checklist for non-profits"
        }
      });
    }
    
    return schemas;
  };

  // Process content to extract FAQs and download sections
  const processContent = (content: string) => {
    if (!content) return { processedContent: '', faqs: [] };
    
    // Extract FAQs
    const faqs: { question: string; answer: string }[] = [];
    
    if (slug === 'why-non-profits-cant-afford-ignore-ai-any-longer') {
      // Look for existing FAQs in the content
      const faqSectionRegex = /<h2>Frequently Asked Questions.*?<\/h2>([\s\S]*?)(?=<h2>|$)/i;
      const faqMatch = content.match(faqSectionRegex);
      
      if (faqMatch && faqMatch[1]) {
        const faqContent = faqMatch[1];
        const questionAnswerRegex = /<h3>(.*?)<\/h3>\s*<p>([\s\S]*?)(?=<h3>|$)/g;
        
        let match;
        while ((match = questionAnswerRegex.exec(faqContent)) !== null) {
          if (match[1] && match[2]) {
            faqs.push({
              question: match[1].trim(),
              answer: match[2].trim()
            });
          }
        }
        
        // Remove the FAQ section from the content as we'll render it separately
        content = content.replace(faqSectionRegex, '');
      }
      
      // If no FAQs were found, add default ones
      if (faqs.length === 0) {
        faqs.push(
          {
            question: "How can small non-profits in Omaha afford to implement AI?",
            answer: "Many AI tools have free or low-cost tiers that are perfect for non-profits with limited budgets. Additionally, organizations in Omaha and throughout Nebraska can apply for technology grants or partner with local universities for support. One80Labs offers special pricing for non-profit organizations in the Midwest region."
          },
          {
            question: "What are some simple AI tools that non-profits in Nebraska can start with?",
            answer: "Non-profits in Omaha, Council Bluffs, and throughout the Midwest can start with user-friendly tools like ChatGPT for content creation, Canva's AI features for design work, or Google's Gemini for data analysis. These tools require minimal technical expertise but can significantly improve operational efficiency."
          },
          {
            question: "How can foundations and grantmakers use AI to improve their operations?",
            answer: "Foundations and grantmakers can use AI to streamline grant application reviews, identify funding patterns, improve donor communications, and measure program outcomes more effectively. AI can help process large volumes of applications and identify the most promising projects based on historical success factors."
          },
          {
            question: "Are there any ethical concerns specific to non-profits using AI?",
            answer: "Yes, non-profits must be particularly careful about data privacy, algorithmic bias, and transparency when using AI. Since many non-profits work with vulnerable populations, ensuring ethical AI use is crucial. Our Non-Profit AI Ethics Checklist provides guidance specific to organizations in the Midwest region."
          },
          {
            question: "How can non-profits in the Midwest find AI training for their staff?",
            answer: "One80Labs offers specialized AI training workshops for non-profits in Omaha, Lincoln, Council Bluffs, and throughout the Midwest. Additionally, local universities, community colleges, and tech meetups often provide affordable training options. Online platforms like Coursera also offer non-profit discounts for AI courses."
          }
        );
      }
    }
    
    // Add local context for Omaha and Midwest non-profits if it's the right post
    if (slug === 'why-non-profits-cant-afford-ignore-ai-any-longer') {
      // Add a section about local resources if it doesn't exist
      if (!content.includes('Midwest Non-Profit AI Resources')) {
        content += `
          <h2>Midwest Non-Profit AI Resources</h2>
          <p>Non-profit organizations in Omaha, Nebraska, Council Bluffs, and throughout the Midwest have unique opportunities to leverage AI technology. Here are some regional resources to help you get started:</p>
          <ul>
            <li>One80Labs AI Training Workshops - Specialized for Omaha-area non-profits</li>
            <li>Nebraska Tech Collaborative - Offers technology grants for non-profits</li>
            <li>Omaha Community Foundation - Technology innovation funding</li>
            <li>University of Nebraska AI Labs - Academic partnerships for non-profits</li>
            <li>Midwest Non-Profit Technology Network - Peer learning and support</li>
          </ul>
          <p>As the premier AI agency in the Midwest, One80Labs is committed to helping non-profits, foundations, and grantmakers in our region implement AI solutions that align with their missions and values.</p>
        `;
      }
    }
    
    // Process download sections
    const downloadSectionRegex = /<div id="blog-download-section"[^>]*data-pdf-name="([^"]*)"[^>]*data-title="([^"]*)"[^>]*data-description="([^"]*)"[^>]*data-button-text="([^"]*)"[^>]*data-source="([^"]*)"[^>]*>[\s\S]*?<\/div>/g;
    
    // Create an array to collect all download section data
    const downloadSections: Array<{
      pdfName: string;
      title: string;
      description: string;
      buttonText: string;
      source: string;
      fullMatch: string;
    }> = [];
    
    // Find all download sections and remove them from the content
    let processedContent = content;
    let match;
    
    // Find all download sections
    while ((match = downloadSectionRegex.exec(content)) !== null) {
      downloadSections.push({
        pdfName: match[1],
        title: match[2],
        description: match[3],
        buttonText: match[4],
        source: match[5],
        fullMatch: match[0]
      });
      
      // Remove the download section from the content
      processedContent = processedContent.replace(match[0], `<!-- DOWNLOAD_SECTION_${downloadSections.length - 1} -->`);
    }
    
    // Remove any duplicate download sections that might be in the content
    // This is the key fix for the duplication issue
    const duplicateRegex = /<div class="my-8 p-6 bg-gray-50 rounded-lg border border-gray-200">[\s\S]*?<h3 class="text-xl font-semibold text-gray-900 mb-4">.*?<\/h3>[\s\S]*?<p class="text-gray-600 mb-4">[\s\S]*?<\/p>[\s\S]*?<\/div>/g;
    processedContent = processedContent.replace(duplicateRegex, '');
    
    return { processedContent, faqs, downloadSections };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="flex flex-col items-center">
          <Loader className="h-8 w-8 text-[#F98B3D] animate-spin mb-4" />
          <p className="text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        {!previewMode && (
          <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        )}
        
        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-red-500 mt-0.5" />
            <div className="ml-4">
              <h2 className="text-lg font-medium text-red-800">Blog Post Not Found</h2>
              <div className="mt-2 text-sm text-red-700">
                {error === 'This blog post is not published yet' ? (
                  <>
                    <p>This blog post is not published yet. Please check back later.</p>
                    {!isPreview && (
                      <p className="mt-2">
                        If you're an admin, you can preview this post from the admin dashboard.
                      </p>
                    )}
                  </>
                ) : (
                  <p>The blog post you're looking for could not be found. It may have been moved or deleted.</p>
                )}
              </div>
              {!previewMode && (
                <div className="mt-4">
                  <Link
                    to="/blog"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                  >
                    View All Blog Posts
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
          <p className="text-yellow-700">Blog post not found</p>
          {!previewMode && (
            <Link to="/blog" className="text-yellow-700 underline mt-2 inline-block">
              Return to blog
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Process content and extract FAQs and download sections
  const { processedContent, faqs, downloadSections } = processContent(post.content);

  // Split the content by download section placeholders
  const contentParts: string[] = [];
  const downloadPlaceholderRegex = /<!-- DOWNLOAD_SECTION_(\d+) -->/g;
  let lastIndex = 0;
  let placeholderMatch;
  
  while ((placeholderMatch = downloadPlaceholderRegex.exec(processedContent)) !== null) {
    // Add content before this placeholder
    contentParts.push(processedContent.substring(lastIndex, placeholderMatch.index));
    // Update lastIndex to after this placeholder
    lastIndex = placeholderMatch.index + placeholderMatch[0].length;
  }
  
  // Add any remaining content after the last placeholder
  contentParts.push(processedContent.substring(lastIndex));

  return (
    <>
      {post && !previewMode && (
        <SEO
          title={`${post.title} | One80Labs Blog - AI for Non-Profits in Omaha & the Midwest`}
          description={post.excerpt || 'Learn how non-profits in Omaha, Nebraska, Council Bluffs, and throughout the Midwest can leverage AI technology to improve operations, increase impact, and better serve their communities.'}
          image={post.image_url}
          article={true}
          jsonLd={getJsonLd()}
          author={post.blog_authors?.name}
          publishedTime={post.published_at}
          modifiedTime={post.updated_at}
          section={post.blog_categories?.name}
          tags={post.blog_tag_posts?.map(tag => tag.blog_tags.name) || []}
          keywords={[
            'non-profit AI Omaha',
            'foundation technology Nebraska',
            'grantmaker AI tools',
            'Midwest non-profit technology',
            'Council Bluffs non-profit AI',
            'Nebraska AI implementation',
            'AI ethics for non-profits',
            'Omaha technology consulting',
            'non-profit automation',
            'AI for foundations',
            'Midwest grantmakers',
            'non-profit digital transformation',
            post.blog_categories?.name,
            ...(post.blog_tag_posts?.map(tag => tag.blog_tags.name) || [])
          ]}
        />
      )}
      <article className="max-w-4xl mx-auto">
        <header className="mb-8">
          <h1 className="blog-title">{post.title}</h1>
          <div className="blog-meta">
            <time dateTime={post.published_at || post.created_at} className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {new Date(post.published_at || post.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <span>•</span>
            <span className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {post.read_time} min read
            </span>
            {post.blog_authors && (
              <>
                <span>•</span>
                <span className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  {post.blog_authors.name}
                </span>
              </>
            )}
            {!post.is_published && (
              <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                Draft
              </span>
            )}
          </div>
        </header>

        {post.image_url && (
          <img 
            className="w-full h-64 object-cover rounded-lg mb-8"
            src={post.image_url}
            alt={post.title}
          />
        )}

        <div className="prose max-w-none mb-12">
          {downloadSections.length > 0 ? (
            // Render content with download sections interspersed
            <>
              {contentParts.map((part, index) => (
                <React.Fragment key={index}>
                  {/* Render content part */}
                  <div dangerouslySetInnerHTML={{ __html: part }} />
                  
                  {/* Render download section if there is one for this index */}
                  {index < downloadSections.length && (
                    <BlogDownloadSection
                      title={downloadSections[index].title}
                      description={downloadSections[index].description}
                      buttonText={downloadSections[index].buttonText}
                      pdfName={downloadSections[index].pdfName}
                      source={downloadSections[index].source}
                    />
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            // If no download sections, just render the content
            <div dangerouslySetInnerHTML={{ __html: processedContent }} />
          )}
        </div>

        {/* Render collapsible FAQs if available */}
        {faqs.length > 0 && (
          <div className="mb-12">
            <CollapsibleFAQ 
              faqs={faqs} 
              title="Frequently Asked Questions About AI for Non-Profits"
            />
          </div>
        )}

        {/* Add a fallback download section at the bottom if needed */}
        {slug === 'why-non-profits-cant-afford-ignore-ai-any-longer' && downloadSections.length === 0 && (
          <BlogDownloadSection
            title="Free Resource for Non-Profits in Omaha and the Midwest"
            description="Get our comprehensive checklist to help your non-profit organization in Omaha, Nebraska, Council Bluffs, or anywhere in the Midwest implement AI ethically and effectively. Perfect for foundations and grantmakers looking to modernize operations."
            buttonText="Download the Non-Profit AI Ethics Checklist (PDF)"
            pdfName="Nonprofit_AI_Ethics_Checklist_v2.pdf"
            source="Blog Post Footer Download"
          />
        )}

        <div className="border-t border-gray-200 pt-6 space-y-6">
          {post.blog_authors && (
            <div className="flex items-center space-x-4">
              {post.blog_authors.image_url && (
                <img 
                  className="h-12 w-12 rounded-full"
                  src={post.blog_authors.image_url}
                  alt={post.blog_authors.name}
                />
              )}
              <div>
                <h3 className="text-lg font-medium text-gray-900">{post.blog_authors.name}</h3>
                <p className="text-gray-600">{post.blog_authors.bio}</p>
              </div>
            </div>
          )}

          {post.blog_tag_posts && post.blog_tag_posts.length > 0 && (
            <div className="flex items-center flex-wrap gap-2">
              <Tag className="h-4 w-4 text-gray-400" />
              {post.blog_tag_posts.map((tagPost: any) => (
                tagPost.blog_tags && (
                  <button
                    key={tagPost.blog_tags.id}
                    onClick={() => handleTagClick(tagPost.blog_tags.name)}
                    className="text-xs font-medium text-gray-500 hover:text-[#F98B3D] transition-colors duration-200"
                  >
                    #{tagPost.blog_tags.name}
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        <div className="mt-12">
          <DynamicCTA 
            type="random" 
            onContactClick={() => setIsContactModalOpen(true)}
            onCalendarClick={() => setIsCalOpen(true)}
            blogPost={post}
          />
        </div>
      </article>

      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />

      <CalWidget
        isOpen={isCalOpen}
        onClose={() => setIsCalOpen(false)}
      />

      {pdfUrl && (
        <EmailCaptureModal
          isOpen={isDownloadModalOpen}
          onClose={() => setIsDownloadModalOpen(false)}
          downloadUrl={pdfUrl}
          source="Download the Non-Profit AI Ethics Checklist"
          title="Download Your Free Resource"
          description="Please enter your name and email to receive the Non-Profit AI Ethics Checklist. This comprehensive guide will help your non-profit organization implement AI ethically and effectively in Omaha, Nebraska, and throughout the Midwest region."
        />
      )}
    </>
  );
}